/**
 * PlatformAnalyticsPage — iter196. Super-admin-only adoption dashboard.
 *
 * Two tiles:
 *   1. Passkey adoption (Face ID / Touch ID / Windows Hello / fingerprint)
 *      — overall % + post-login prompt funnel + by-source breakdown
 *   2. PWA install adoption — overall % + funnel + per-device-class breakdown
 *
 * Surfaces only the metrics relevant to product decisions about login UX
 * (passkey rollout) and shortcut adoption (PWA install) — exactly the two
 * surfaces we just shipped in iter195 & iter195.5.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Fingerprint, Smartphone, RefreshCw, Loader2, TrendingUp, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';

function pct(n) {
  if (!Number.isFinite(n)) return '0.0%';
  return `${n.toFixed(1)}%`;
}

function Stat({ label, value, sub, testId }) {
  return (
    <div className="rounded-xl border-2 border-border bg-card px-3 py-2" data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.16em] font-bold text-muted-foreground">{label}</p>
      <p className="text-xl font-display font-bold tabular-nums leading-tight mt-0.5">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function FunnelRow({ label, count, denom, testId }) {
  const ratio = denom > 0 ? (count / denom) * 100 : 0;
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-xs font-semibold mb-1">
        <span>{label}</span>
        <span className="tabular-nums">{count.toLocaleString()} <span className="text-muted-foreground font-normal">({pct(ratio)})</span></span>
      </div>
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${Math.min(100, ratio)}%` }}
        />
      </div>
    </div>
  );
}

export default function PlatformAnalyticsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/analytics/summary');
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!user?.is_super_admin) {
    return (
      <div className="min-h-screen p-6 flex flex-col items-center justify-center text-center">
        <p className="text-sm text-muted-foreground" data-testid="analytics-forbidden">
          Super admin access required to view platform analytics.
        </p>
        <Button onClick={() => navigate(-1)} className="mt-4">Go back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="platform-analytics-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          data-testid="analytics-back-btn"
          className="p-1 rounded-full hover:bg-muted transition"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-lg font-bold leading-tight">Platform Analytics</h1>
          <p className="text-xs text-muted-foreground truncate">
            Login UX & shortcut adoption — super-admin only
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          aria-label="Refresh"
          data-testid="analytics-refresh-btn"
          className="p-2 rounded-full hover:bg-muted transition disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </div>

      {error && (
        <div className="m-4 rounded-xl border-2 border-destructive bg-destructive/10 px-3 py-2 text-xs text-destructive" data-testid="analytics-error">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="m-6 text-center text-sm text-muted-foreground inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading metrics…
        </div>
      )}

      {data && (
        <div className="px-4 py-5 space-y-6">
          {/* Passkey adoption */}
          <section className="rounded-2xl border-2 border-border bg-card p-4 space-y-4" data-testid="passkey-adoption-tile">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
                <Fingerprint className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-display text-base font-bold">Passkey adoption</h2>
                <p className="text-xs text-muted-foreground">Face ID / Touch ID / Windows Hello / fingerprint</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Stat
                label="Adoption"
                value={pct(data.passkey.adoption_pct)}
                sub={`${data.passkey.users_with_passkey} of ${data.passkey.total_users}`}
                testId="passkey-stat-adoption"
              />
              <Stat
                label="Enrolled"
                value={data.passkey.enrolled_total_distinct_users.toLocaleString()}
                sub="distinct users"
                testId="passkey-stat-enrolled"
              />
              <Stat
                label="Prompt CVR"
                value={pct(data.passkey.prompt_conversion_pct)}
                sub="post-login modal"
                testId="passkey-stat-conversion"
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.16em] font-bold text-muted-foreground">
                Post-login modal funnel
              </p>
              <FunnelRow
                label="Prompt shown"
                count={data.passkey.funnel.shown}
                denom={data.passkey.funnel.shown}
                testId="passkey-funnel-shown"
              />
              <FunnelRow
                label="Dismissed"
                count={data.passkey.funnel.dismissed}
                denom={data.passkey.funnel.shown}
                testId="passkey-funnel-dismissed"
              />
              <FunnelRow
                label="Enrolled (from prompt)"
                count={data.passkey.funnel.enrolled_via_prompt}
                denom={data.passkey.funnel.shown}
                testId="passkey-funnel-enrolled-prompt"
              />
            </div>

            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs flex items-center justify-between" data-testid="passkey-by-source">
              <span className="font-semibold inline-flex items-center gap-1.5">
                <TrendingUp className="w-3 h-3" /> By source
              </span>
              <span className="tabular-nums">
                Prompt {data.passkey.funnel.enrolled_via_prompt} · Profile {data.passkey.funnel.enrolled_via_profile}
              </span>
            </div>
          </section>

          {/* PWA install adoption */}
          <section className="rounded-2xl border-2 border-border bg-card p-4 space-y-4" data-testid="pwa-adoption-tile">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-display text-base font-bold">PWA install adoption</h2>
                <p className="text-xs text-muted-foreground">Home-screen shortcut on mobile + desktop</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Stat
                label="Adoption"
                value={pct(data.pwa.install_pct)}
                sub={`${data.pwa.installed_distinct_users} of ${data.pwa.total_users}`}
                testId="pwa-stat-adoption"
              />
              <Stat
                label="Accept CVR"
                value={pct(data.pwa.accept_pct)}
                sub="of users prompted"
                testId="pwa-stat-acceptcvr"
              />
              <Stat
                label="Total shown"
                value={data.pwa.funnel.shown.toLocaleString()}
                sub="distinct users"
                testId="pwa-stat-shown"
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.16em] font-bold text-muted-foreground">
                Install funnel
              </p>
              <FunnelRow
                label="Prompt shown"
                count={data.pwa.funnel.shown}
                denom={data.pwa.funnel.shown}
                testId="pwa-funnel-shown"
              />
              <FunnelRow
                label="Dismissed"
                count={data.pwa.funnel.dismissed + data.pwa.funnel.ios_dismissed}
                denom={data.pwa.funnel.shown}
                testId="pwa-funnel-dismissed"
              />
              <FunnelRow
                label="Installed"
                count={data.pwa.funnel.installed}
                denom={data.pwa.funnel.shown}
                testId="pwa-funnel-installed"
              />
            </div>

            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs grid grid-cols-3 gap-2" data-testid="pwa-by-device">
              <div>
                <p className="font-semibold text-muted-foreground">Mobile</p>
                <p className="text-sm font-bold tabular-nums">{data.pwa.by_device.mobile}</p>
              </div>
              <div>
                <p className="font-semibold text-muted-foreground">Desktop</p>
                <p className="text-sm font-bold tabular-nums">{data.pwa.by_device.desktop}</p>
              </div>
              <div>
                <p className="font-semibold text-muted-foreground">Tablet</p>
                <p className="text-sm font-bold tabular-nums">{data.pwa.by_device.tablet}</p>
              </div>
            </div>
          </section>

          <p className="text-xs text-muted-foreground text-center" data-testid="analytics-generated-at">
            <Users className="w-3 h-3 inline-block mr-1" />
            Generated {new Date(data.generated_at).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}
